import { useState, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Check } from "lucide-react";

interface Task {
  id: string;
  text: string;
  completed: boolean;
  level: number; // 0 = header, 1 = main task, 2 = subtask
  isHeader: boolean;
}

interface TaskListParserProps {
  text: string;
  onTasksChange?: (tasks: Task[]) => void;
}

export const TaskListParser = ({
  text,
  onTasksChange,
}: TaskListParserProps) => {
  const [tasks, setTasks] = useState<Task[]>([]);

  const parseTextToTasks = (inputText: string) => {
    const lines = inputText.split("\n");
    const parsedTasks: Task[] = [];
    let taskId = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Skip empty lines
      if (!line) continue;

      // Check if it's a header (lines with ## or just bold/title text)
      const isMainHeader = line.match(/^#+\s+(.+)/) || line.match(/^✅\s+(.+)/);
      const isSubheader =
        line.match(/^\d+\.\s+\*\*(.+)\*\*/) || line.match(/^[A-Z][^:]*:$/);

      if (isMainHeader) {
        const headerText = isMainHeader[1] || line;
        parsedTasks.push({
          id: `task-${taskId++}`,
          text: headerText,
          completed: line.startsWith("✅"),
          level: 0,
          isHeader: true,
        });
      } else if (isSubheader) {
        const headerText = isSubheader[1] || line.replace(":", "");
        parsedTasks.push({
          id: `task-${taskId++}`,
          text: headerText,
          completed: false,
          level: 1,
          isHeader: true,
        });
      } else {
        // Check for task items (lines starting with checkboxes or bullets)
        const checkboxMatch = line.match(/^[-*]\s+\[([x\s])\]\s+(.+)/);
        const bulletMatch = line.match(/^[-*•]\s+(.+)/);

        if (checkboxMatch) {
          parsedTasks.push({
            id: `task-${taskId++}`,
            text: checkboxMatch[2],
            completed: checkboxMatch[1].toLowerCase() === "x",
            level: 2,
            isHeader: false,
          });
        } else if (bulletMatch) {
          // Determine level based on indentation or content
          const isSubTask = line.startsWith("  ") || line.startsWith("\t");
          parsedTasks.push({
            id: `task-${taskId++}`,
            text: bulletMatch[1],
            completed: false,
            level: isSubTask ? 2 : 1,
            isHeader: false,
          });
        } else if (line.match(/^\d+\.\s+(.+)/)) {
          // Numbered lists
          const numberedMatch = line.match(/^\d+\.\s+(.+)/);
          if (numberedMatch) {
            parsedTasks.push({
              id: `task-${taskId++}`,
              text: numberedMatch[1],
              completed: false,
              level: 1,
              isHeader: false,
            });
          }
        }
      }
    }

    setTasks(parsedTasks);
    onTasksChange?.(parsedTasks);
  };

  useEffect(() => {
    parseTextToTasks(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const toggleTask = (taskId: string) => {
    const updatedTasks = tasks.map((task) =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    );
    setTasks(updatedTasks);
    onTasksChange?.(updatedTasks);
  };

  const getCompletionStats = () => {
    const taskItems = tasks.filter((t) => !t.isHeader);
    const completed = taskItems.filter((t) => t.completed).length;
    const total = taskItems.length;
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  };

  const stats = getCompletionStats();

  const progressStyle = {
    "--progress-width": `${stats.percentage}%`,
  } as React.CSSProperties;

  return (
    <div className="h-full flex flex-col">
      {/* Progress Header */}
      <div className="p-4 border-b bg-muted/30" style={progressStyle}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Task Progress</h3>
          <span className="text-xs text-muted-foreground">
            {stats.completed} / {stats.total} ({stats.percentage}%)
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            className="bg-primary h-full rounded-full transition-all duration-300 [width:var(--progress-width)]"
            data-testid="progress-bar"
          />
        </div>
      </div>

      {/* Task List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {tasks.map((task) => {
            if (task.isHeader) {
              return (
                <div
                  key={task.id}
                  className={`font-semibold ${
                    task.level === 0
                      ? "text-lg mt-4 mb-2 text-primary"
                      : "text-sm mt-3 mb-1 text-foreground"
                  }`}
                >
                  {task.completed && (
                    <Check className="inline w-4 h-4 mr-1 text-green-600" />
                  )}
                  {task.text}
                </div>
              );
            }

            return (
              <Card
                key={task.id}
                className={`p-3 cursor-pointer transition-all hover:shadow-md ${
                  task.completed ? "bg-muted/50 opacity-75" : ""
                } ${
                  task.level === 2 ? "ml-6" : task.level === 1 ? "ml-3" : ""
                }`}
                onClick={() => toggleTask(task.id)}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={task.completed}
                    onCheckedChange={() => toggleTask(task.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <p
                      className={`text-sm ${
                        task.completed
                          ? "line-through text-muted-foreground"
                          : "text-foreground"
                      }`}
                    >
                      {task.text}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
